import React from 'react';
import {
    PremiumChevronDownIcon,
    PremiumLocationPinIcon,
    PremiumMedalIcon,
    PremiumSwapIcon,
    PremiumTrendUpIcon,
    PremiumUserIcon,
} from './PremiumHeatmapIcons';
import { RedemptionTrend, LocationPerformance, UserEngagement } from '../MerchantHeatmap.data';

interface PremiumInsightsBarProps {
    peakDay: string;
    topLocation: string;
    avgCivicPoints: number;
    redemptionTrends: RedemptionTrend[];
    locationPerformance: LocationPerformance;
    userEngagement: UserEngagement;
}

export const PremiumInsightsBar: React.FC<PremiumInsightsBarProps> = ({ 
    peakDay, 
    topLocation, 
    avgCivicPoints,
    redemptionTrends,
    locationPerformance,
    userEngagement 
}) => {
    const [expanded, setExpanded] = React.useState(false);

    // Calculate max redemption count for chart scaling
    const maxRedemptions = React.useMemo(() => {
        return Math.max(...redemptionTrends.map(t => t.redemption_count), 1);
    }, [redemptionTrends]);

    // Calculate peak redemption count based on highest redemptions in trends
    const peakRedemptions = React.useMemo(() => {
        if (redemptionTrends.length === 0) return 0;
        // Match peak day (full name like "Wednesday") with date in trends (short like "Wed")
        const peakDayShort = peakDay.substring(0, 3);
        const peakTrend = redemptionTrends.find(t => t.date === peakDayShort);
        return peakTrend?.redemption_count || 0;
    }, [redemptionTrends, peakDay]);

    const Chevron = ({ expanded }: { expanded: boolean }) => (
        <div className="shrink-0 py-[8px] pl-[24px] pr-[8px] flex items-center justify-center">
            <div
                className="w-[20px] h-[20px] flex items-center justify-center transition-transform duration-200"
                style={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transformOrigin: '50% 50%',
                }}
            >
                <PremiumChevronDownIcon size={20} className="text-[#4A5565] dark:text-gray-300" />
            </div>
        </div>
    );

    const SectionHeader = ({ title }: { title: string }) => (
        <div style={{ width: '521.2px', justifyContent: 'flex-start', alignItems: 'center', gap: '8px', display: 'inline-flex' }}>
            <div style={{ width: '4px', height: '24px', background: '#00C49A', borderRadius: '26843500px' }} />
            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                <div className="dark:text-white" style={{ justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#364153', fontSize: '18px', fontFamily: 'Segoe UI', fontWeight: 600, lineHeight: '28px' }}>
                    {title}
                </div>
            </div>
        </div>
    );

    const MetricTile = ({
        label,
        icon,
        value,
        subtext,
        valueFontSize = 24,
        valueLineHeight = 32,
    }: {
        label: React.ReactNode;
        icon: React.ReactNode;
        value: React.ReactNode;
        subtext: React.ReactNode;
        valueFontSize?: number;
        valueLineHeight?: number;
    }) => (
        <div className="bg-white dark:bg-gray-800 dark:outline-gray-700" style={{ flex: '1 1 0', alignSelf: 'stretch', padding: '19.8px', borderRadius: '14px', outline: '1px #E6E6E6 solid', outlineOffset: '-1px', display: 'inline-flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
                <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                    <div className="dark:text-white" style={{ justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#364153', fontSize: '12px', fontFamily: 'Segoe UI', fontWeight: 600, lineHeight: '16px' }}>
                        {label}
                    </div>
                </div>
                <div className="bg-[#F2FCFA] dark:bg-gray-700" style={{ padding: '8px', borderRadius: '10px', display: 'inline-flex' }}>
                    {icon}
                </div>
            </div>
            <div style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div className="dark:text-white" style={{ color: '#364153', fontSize: `${valueFontSize}px`, fontFamily: 'Segoe UI', fontWeight: 700, lineHeight: `${valueLineHeight}px` }}>
                    {value}
                </div>
                <div className="dark:text-gray-200" style={{ color: '#4A5565', fontSize: '12px', fontFamily: 'Segoe UI', fontWeight: 400, lineHeight: '16px' }}>
                    {subtext}
                </div>
            </div>
        </div>
    );

    return (
        <div
            className="w-full overflow-hidden rounded-[14px] outline outline-1 outline-[#E6E6E6] dark:outline-gray-700 outline-offset-[-1px]"
            style={{
                background: expanded ? 'linear-gradient(90deg, #F2FCFA 0%, #FFF9F0 100%)' : 'white',
            }}
        >
            <style>{`
                .dark .w-full.overflow-hidden.rounded-\[14px\] {
                    background: ${expanded ? 'linear-gradient(90deg, rgb(6 78 59) 0%, rgb(120 53 15) 100%)' : 'rgb(31 41 55)'} !important;
                }
            `}</style>
            {/* Header row (clickable) */}
            <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="w-full text-left bg-white dark:bg-gray-800"
            >
                <div className="w-full px-[20px] py-[16px] flex items-center justify-between">
                    <div className="flex-1 flex flex-wrap content-center items-center justify-start gap-[260.2px]">
                        {/* Peak Day */}
                        <div className="flex items-center gap-[12px]">
                            <div className="p-[10px] bg-[#F2FCFA] dark:bg-emerald-950/30 overflow-hidden rounded-[10px] inline-flex">
                                <PremiumTrendUpIcon size={20} className="text-[#00C49A]" />
                            </div>
                            <div className="inline-flex flex-col">
                                <div className="text-[#4A5565] dark:text-gray-300 text-[12px] font-normal leading-[16px]" style={{ fontFamily: 'Segoe UI' }}>
                                    Peak Day
                                </div>
                                <div className="text-[#364153] dark:text-white text-[14px] font-bold leading-[20px]" style={{ fontFamily: 'Segoe UI' }}>
                                    {peakDay}
                                </div>
                            </div>
                        </div>

                        {/* Top Location */}
                        <div className="flex items-center gap-[12px]">
                            <div className="p-[10px] bg-[#F2FCFA] dark:bg-emerald-950/30 overflow-hidden rounded-[10px] inline-flex">
                                <PremiumLocationPinIcon size={20} className="text-[#00C49A]" />
                            </div>
                            <div className="inline-flex flex-col">
                                <div className="text-[#4A5565] dark:text-gray-300 text-[12px] font-normal leading-[16px]" style={{ fontFamily: 'Segoe UI' }}>
                                    Top Location
                                </div>
                                <div className="text-[#364153] dark:text-white text-[14px] font-bold leading-[20px]" style={{ fontFamily: 'Segoe UI' }}>
                                    {topLocation}
                                </div>
                            </div>
                        </div>

                        {/* Avg. Civic Points */}
                        <div className="flex items-center gap-[12px]">
                            <div className="p-[10px] bg-[#F2FCFA] dark:bg-emerald-950/30 overflow-hidden rounded-[10px] inline-flex">
                                <PremiumMedalIcon size={20} className="text-[#00C49A]" />
                            </div>
                            <div className="inline-flex flex-col">
                                <div className="text-[#4A5565] dark:text-gray-300 text-[12px] font-normal leading-[16px]" style={{ fontFamily: 'Segoe UI' }}>
                                    Avg. Civic Points
                                </div>
                                <div className="text-[#364153] dark:text-white text-[14px] font-bold leading-[20px]" style={{ fontFamily: 'Segoe UI' }}>
                                    {avgCivicPoints}
                                </div>
                            </div>
                        </div>
                    </div>

                    <Chevron expanded={expanded} />
                </div>
            </button>

            {/* Expanded content (node-id=21265:12768) */}
            {expanded && (
                <div
                    className="w-full bg-white dark:bg-gray-800 border-t border-[#E6E6E6] dark:border-gray-700"
                >
                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div style={{ alignSelf: 'stretch', height: '534px', display: 'inline-flex', flexWrap: 'wrap', alignContent: 'center', gap: '24px', alignItems: 'center' }}>
                            {/* Left column */}
                            <div style={{ flex: '1 1 0', alignSelf: 'stretch', display: 'flex', gap: '20px' }}>
                                <div style={{ flex: '1 1 0', display: 'inline-flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* Redemption Trends */}
                                    <div className="bg-white dark:bg-gray-800 dark:outline-gray-700" style={{ alignSelf: 'stretch', height: '408px', padding: '23.8px', borderRadius: '14px', outline: '1px #E6E6E6 solid', outlineOffset: '-1px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                        <SectionHeader title="Redemption Trends (Last 7 Days)" />

                                        {/* Chart with dynamic data */}
                                        <div style={{ alignSelf: 'stretch', height: '242px', paddingTop: '8px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                            <div style={{ alignSelf: 'stretch', flex: '1 1 0', position: 'relative' }}>
                                                {/* Day labels */}
                                                {redemptionTrends.map((trend, idx) => (
                                                    <div key={trend.date} className="dark:text-gray-300" style={{ width: '24.4px', height: '14.04px', left: `${67.05 + idx * 60.47}px`, top: '205.47px', position: 'absolute', textAlign: 'center', color: '#9CA3AF', fontSize: '12px', fontFamily: 'Inter', fontWeight: 400 }}>
                                                        {trend.date}
                                                    </div>
                                                ))}
                                                {/* Y-axis labels */}
                                                {[
                                                    { top: 194, text: '0' },
                                                    { top: 144.86, text: Math.round(maxRedemptions * 0.25).toString() },
                                                    { top: 95.72, text: Math.round(maxRedemptions * 0.5).toString() },
                                                    { top: 46.58, text: Math.round(maxRedemptions * 0.75).toString() },
                                                    { top: 1.18, text: maxRedemptions.toString() },
                                                ].map((y) => (
                                                    <div key={y.text} className="dark:text-gray-300" style={{ width: '15.02px', height: '14.04px', left: '38.48px', top: `${y.top}px`, position: 'absolute', textAlign: 'right', color: '#9CA3AF', fontSize: '12px', fontFamily: 'Inter', fontWeight: 400 }}>
                                                        {y.text}
                                                    </div>
                                                ))}
                                                {/* Bars */}
                                                {redemptionTrends.map((trend, idx) => {
                                                    const barHeight = maxRedemptions > 0 ? (trend.redemption_count / maxRedemptions) * 191.1 : 0;
                                                    const barTop = 201.24 - barHeight;
                                                    return (
                                                        <div 
                                                            key={`bar-${idx}`} 
                                                            style={{ 
                                                                width: '47.87px', 
                                                                height: `${barHeight}px`, 
                                                                left: `${67.05 + idx * 60.47}px`, 
                                                                top: `${barTop}px`, 
                                                                position: 'absolute', 
                                                                background: '#00C49A' 
                                                            }} 
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Peak day callout */}
                                        <div className="bg-[#F2FCFA] dark:bg-gray-700" style={{ alignSelf: 'stretch', padding: '12px', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                            <PremiumTrendUpIcon size={16} className="text-[#00C49A]" />
                                            <div className="dark:text-gray-100" style={{ color: '#364153', fontSize: '14px', fontFamily: 'Segoe UI', lineHeight: '20px' }}>
                                                Peak day: <span style={{ fontWeight: 600 }}>{peakDay}</span> with {peakRedemptions} redemptions
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right column */}
                            <div style={{ width: '570.8px', alignSelf: 'stretch', position: 'relative', display: 'inline-flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ alignSelf: 'stretch', flex: '1 1 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* Location Performance */}
                                    <div className="bg-white dark:bg-gray-800 dark:outline-gray-700" style={{ alignSelf: 'stretch', padding: '23.8px', borderRadius: '14px', outline: '1px #E6E6E6 solid', outlineOffset: '-1px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                        <SectionHeader title="Location Performance" />
                                        <div style={{ width: '521.2px', justifyContent: 'center', alignItems: 'flex-start', gap: '20px', display: 'inline-flex' }}>
                                            <MetricTile
                                                label={<span>Total<br />Redemptions</span>}
                                                icon={
                                                    <PremiumTrendUpIcon size={16} className="text-[#00C49A]" />
                                                }
                                                value={locationPerformance.total_redemptions}
                                                subtext="Last 7 days"
                                            />
                                            <MetricTile
                                                label={<span>Top <br />Location</span>}
                                                icon={
                                                    <PremiumLocationPinIcon size={16} className="text-[#00C49A]" />
                                                }
                                                value={locationPerformance.top_location.location_name}
                                                subtext={`${locationPerformance.top_location.percentage.toFixed(1)}% of redemptions`}
                                                valueFontSize={16}
                                                valueLineHeight={28}
                                            />
                                            <MetricTile
                                                label={<span>Avg. Per<br />Location</span>}
                                                icon={
                                                    <PremiumSwapIcon size={16} className="text-[#00C49A]" />
                                                }
                                                value={locationPerformance.avg_per_location}
                                                subtext="Redemptions per area"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* User Engagement (positioned block in HTML) */}
                                <div style={{ width: '570px', height: '271px', left: '0.4px', top: '263.2px', position: 'absolute', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="bg-white dark:bg-gray-800 dark:outline-gray-700" style={{ alignSelf: 'stretch', flex: '1 1 0', padding: '23.8px', borderRadius: '14px', outline: '1px #E6E6E6 solid', outlineOffset: '-1px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                        <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: '8px', display: 'inline-flex' }}>
                                            <div style={{ width: '4px', height: '24px', background: '#00C49A', borderRadius: '26843500px' }} />
                                            <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
                                                <div className="dark:text-white" style={{ justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#364153', fontSize: '18px', fontFamily: 'Segoe UI', fontWeight: 600, lineHeight: '28px' }}>
                                                    User Engagement
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ width: '521.2px', justifyContent: 'center', alignItems: 'flex-start', gap: '20px', display: 'inline-flex' }}>
                                            <MetricTile
                                                label={<span>Avg. <br />Points Spent</span>}
                                                icon={
                                                    <PremiumMedalIcon size={16} className="text-[#00C49A]" />
                                                }
                                                value={userEngagement.avg_points_spent}
                                                subtext="Per redeemer"
                                            />
                                            <MetricTile
                                                label={<span>Top Event Among Your <br />Customers</span>}
                                                icon={
                                                    <PremiumUserIcon size={16} className="text-[#00C49A]" />
                                                }
                                                value={userEngagement.top_event.event_name || 'N/A'}
                                                subtext={userEngagement.top_event.attendees_redeemed > 0 ? `${userEngagement.top_event.attendees_redeemed} attendees redeemed` : 'No data'}
                                                valueFontSize={16}
                                                valueLineHeight={20}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

